import CoveragePackageModalLink from '../CoveragePackageModalLink';
import { GridItem, Box, SelectField } from '@flos/react-ui';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useState, type ChangeEvent } from 'react';
import { useUpdateCoveragePackageInformation } from '../../../../recoil/hooks/useUpdateCoveragePackageInformation';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { deductibleOptionsSelector } from '../../../../recoil/selectors/deductibleOptionsSelector';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCoverable } from '../../../../utils/coverablesUtil';
import type {
    PrivateContentOnsaDTO,
    YouthContentOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/content';
import { refreshCoveragePackagePricesAtom } from '../../../../recoil/atoms/refreshCoveragePackagePricesAtom';

const CoveragePackageContentInput = ({
    isLoading,
    setIsLoading
}: {
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const inputHeaderText = getHippoText('DSS-COVERAGE-PACKAGE-PAGE-DEDUCTIBLE-HEADER');
    const modalName = getHippoText('DSS-COVERAGE-PACKAGE-PAGE-INPUT-INFO');
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const refreshPrices = useSetRecoilState(refreshCoveragePackagePricesAtom);
    const { updateCoveragePackageInformation } = useUpdateCoveragePackageInformation<
        PrivateContentOnsaDTO | YouthContentOnsaDTO
    >();
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { flavourName = '', fixedId = '' } = currentProduct ?? {};
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const coverable = getOnsaCoverable(coverables, flavourName, fixedId) as
        | PrivateContentOnsaDTO
        | YouthContentOnsaDTO;
    const [deductible, setDeductible] = useState(coverable.contentAndBaggageCovDeductible);
    const deductibleOptions = useRecoilValue(deductibleOptionsSelector('PRCContentAndBaggageCov'));

    const changeDeductible = (event: ChangeEvent<HTMLSelectElement>): void => {
        if (event.target.value) {
            setDeductible(event.target.value);
            setIsLoading(true);
            updateCoverableInformation({ contentAndBaggageCovDeductible: event.target.value });
            void updateCoveragePackageInformation({
                contentAndBaggageCovDeductible: event.target.value
            }).then(() => {
                setIsLoading(false);
                refreshPrices({});
            });
        }
    };

    const optionsList = deductibleOptions.map((option) => {
        return { label: option.displayName, value: option.optionID };
    });

    return (
        <GridItem span={7}>
            <Box>
                <div className="u-flex-box u-flex-box--space-between">
                    <h4>{inputHeaderText}</h4>
                    <CoveragePackageModalLink
                        modalName={modalName}
                        title={getHippoText(
                            'DSS-COVERAGE-PACKAGE-PAGE-DEDUCTIBLE-INFO-MODAL-TITLE'
                        )}
                        description={getHippoText(
                            'DSS-COVERAGE-PACKAGE-PAGE-DEDUCTIBLE-INFO-MODAL-DESCRIPTION'
                        )}
                    />
                </div>
                <div className="u-spacing-stack-none">
                    <SelectField
                        placeholder={deductible}
                        onChange={changeDeductible}
                        label={getHippoText('DSS-COVERABLE-DETAILS-DEDUCTIBLE')}
                        options={optionsList}
                        value={deductible}
                        disabled={isLoading}
                    ></SelectField>
                </div>
            </Box>
        </GridItem>
    );
};

export default CoveragePackageContentInput;
